import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Module,
  PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT,
  PATH_PROJECT_INVOICE_SPECIFICATIONS_NEW,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_NEW,
} from "@/constants";
import Title from "@/components/Title";
import { List } from "@/components/list/List";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { SuccessFetchListResponse } from "@/services/api";
import { Button, ButtonShortcut } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { getInvoiceSpecificationsListGetter } from "@/services/invoiceSpecifications";
import { invoiceSpecificationRead } from "@/schemas/invoiceSpecification";
import { generatePath, useNavigate } from "react-router-dom";
import { useInvoiceSpecificationsColumnOverrides } from "@/modules/projects/hooks/useInvoiceSpecificationsSectionColumnOverrides";
import ModKeyChar from "@/components/ModKeyChar";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { ReadInvoiceSpecification } from "@/types/invoiceSpecification";

const getHederTitle = (projectDependsOn: "department" | "role") => {
  switch (projectDependsOn) {
    case "department": {
      return "projectSettlementsInvoiceSpecifications.header.depends_on_departments";
    }
    case "role": {
      return "projectSettlementsInvoiceSpecifications.header.depends_on_roles";
    }
  }
};
interface InvoiceSpecificationsComponentProps {
  projectId: string;
  settlementId: string;
  projectDependsOn: "department" | "role" | undefined;
}
const InvoiceSpecificationsComponent = ({
  projectId,
  settlementId,
  projectDependsOn,
}: InvoiceSpecificationsComponentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const name = "projectSettlementsInvoiceSpecifications";
  const { can_update } = useModulePermissions();
  const columnOverrides = useInvoiceSpecificationsColumnOverrides();
  const options = useCreateDataTableOptions<ReadInvoiceSpecification>(name);
  const routeProjectId = useProjectId();
  const hasProjectIdInRoute = Boolean(routeProjectId);

  const editPathTemplate = hasProjectIdInRoute
    ? PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT
    : PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT;

  const createPathTemplate = hasProjectIdInRoute
    ? PATH_PROJECT_INVOICE_SPECIFICATIONS_NEW
    : PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_NEW;

  const fetchInvoiceSpecificationsList = useCallback(
    getInvoiceSpecificationsListGetter(settlementId),
    [settlementId],
  );
  const queryKey = useDynamicQueryParams(name, options);
  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchInvoiceSpecificationsList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [fetchInvoiceSpecificationsList, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadInvoiceSpecification>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  const onClickCreate = useCallback(() => {
    navigate(
      generatePath(createPathTemplate, {
        settlementId,
        projectId,
      }),
    );
  }, [navigate, createPathTemplate, settlementId, projectId]);

  const headerTitle = useMemo(() => {
    return projectDependsOn ? t(getHederTitle(projectDependsOn)) : "...";
  }, [t, projectDependsOn]);

  const getRowProps = useCallback(function <T>(row: {
    original: T;
  }): Record<string, unknown> {
    const isDeleted = (row.original as ReadInvoiceSpecification).deleted_at;
    return isDeleted ? { className: "bg-red-50/70" } : {};
  }, []);

  const tableFeatures = useMemo(
    () => ({
      exportSettings: { moduleName: Module.InvoiceSpecification },
      getRowProps,
      initialColumnVisibility: { project_name: false },
    }),
    [getRowProps],
  );

  return (
    <>
      <List
        schema={invoiceSpecificationRead}
        name={name}
        query={query}
        options={options}
        pathBuilder={(row) =>
          generatePath(editPathTemplate, {
            settlementId,
            projectId,
            invoiceSpecificationId: row.id,
          })
        }
        tableFeatures={tableFeatures}
        columnOverrides={columnOverrides}
      >
        <div className="py-2">
          <Title>{headerTitle}</Title>
        </div>
        <DataTableQueryContextProvider value={queryValue}>
          <List.Content>
            <List.Status>
              <Button
                size="sm"
                variant="secondary"
                disabled={!can_update}
                onClick={onClickCreate}
              >
                <PlusIcon className="w-4 h-4" />
                {t(
                  "projectSettlementsInvoiceSpecifications.buttons.addInvoiceSpecification",
                )}
                <ButtonShortcut>
                  <ModKeyChar />A
                </ButtonShortcut>
              </Button>
            </List.Status>
          </List.Content>
        </DataTableQueryContextProvider>
      </List>
    </>
  );
};

export const InvoiceSpecificationsSection = memo(
  InvoiceSpecificationsComponent,
);
